import { useState } from "react";
import Navigation from "../components/Navigation";
import { api } from "../lib/api";
import { Search, Sparkles, Loader2, Quote } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Research Memory — semantic search across every panelist response the client owns.
 * Powered by TF-IDF + cosine on the backend (no embedding store needed).
 */
export default function ResearchMemory() {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!q.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/memory/search`, { params: { q, limit: 20 } });
      setResults(data.results || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--rl-bg-subtle)]" data-testid="research-memory-page">
      <Navigation />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-2">
          <Sparkles className="w-4 h-4 text-[var(--rl-brand)]" />
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)]">Research memory</div>
        </div>
        <h1 className="font-display font-black text-4xl tracking-tight">Search every panelist answer.</h1>
        <p className="text-[var(--rl-text-secondary)] mt-3 leading-relaxed">
          One search bar across every interview you've ever run. "Anything about pricing?" "Who mentioned a competitor?" Quotes link back to the source audio.
        </p>

        <form onSubmit={submit} className="mt-8 flex gap-2" data-testid="memory-search-form">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--rl-text-secondary)] pointer-events-none" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="e.g. 'anyone mention price friction'…"
              className="w-full bg-white border border-[var(--rl-border)] pl-9 pr-4 py-3 focus:outline-none focus:border-[var(--rl-brand)]"
              data-testid="memory-search-input"
            />
          </div>
          <button
            type="submit"
            disabled={!q.trim() || loading}
            className="bg-[var(--rl-text)] text-white px-6 py-3 font-semibold hover:bg-[var(--rl-brand)] transition-colors disabled:opacity-40"
            data-testid="memory-search-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
          </button>
        </form>

        {error && <div className="mt-4 text-sm text-[var(--rl-accent)]" data-testid="memory-error">{error}</div>}

        {results !== null && !loading && (
          <div className="mt-8" data-testid="memory-results">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)] mb-3">
              {results.length} {results.length === 1 ? "match" : "matches"}
            </div>
            {results.length === 0 ? (
              <div className="bg-white border border-dashed border-[var(--rl-border)] p-8 text-center text-sm text-[var(--rl-text-secondary)]">
                No matching panelist responses. Try broader wording.
              </div>
            ) : (
              <div className="space-y-3">
                {results.map((r, i) => (
                  <div key={`${r.interview_id}-${r.q_index}`} className="bg-white border border-[var(--rl-border)] p-5" data-testid={`memory-result-${i}`}>
                    <div className="flex items-start gap-3">
                      <Quote className="w-4 h-4 text-[var(--rl-accent)] flex-shrink-0 mt-1" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm leading-relaxed italic">"{r.text}"</div>
                        <div className="mt-3 flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--rl-text-secondary)]">
                          <Link to={`/client/campaigns/${r.campaign_id}/report`} className="hover:text-[var(--rl-text)]">
                            → View campaign report
                          </Link>
                          <span>·</span>
                          <span className="font-mono">Relevance {(r.score * 100).toFixed(0)}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
